import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CustomerServices from './pages/CustomerServices';
import Integrations from './pages/Integrations';
import Xero from './pages/Xero';
import XeroCallback from './pages/XeroCallback';
import Microsoft365 from './pages/Microsoft365';
import PartnerCenter from './pages/PartnerCenter';
import Cove from './pages/Cove';
import SentinelOne from './pages/SentinelOne';
import Proxmox from './pages/Proxmox';
import Billing from './pages/Billing';
import Expenses from './pages/Expenses';
import UserManager from './pages/UserManager';
import OrganizationManager from './pages/OrganizationManager';
import Profile from './pages/Profile';
import Layout from './components/Layout';
import WHMCS from './pages/WHMCS';
import Splynx from './pages/Splynx';
import PortaOne from './pages/PortaOne';
import Redstor from './pages/Redstor';
import AuditLog from './pages/AuditLog';
import HaloPSA from './pages/HaloPSA';
import CloudBlue from './pages/CloudBlue';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* Uploads removed; Customers merged into Invoice Prep */}
          <Route path="/uploads" element={<Navigate to="/dashboard" replace />} />
          <Route path="/comparisons/*" element={<Navigate to="/billing" replace />} />
          <Route path="/exports" element={<Navigate to="/billing" replace />} />
          <Route path="/proposals/*" element={<Navigate to="/billing" replace />} />
          <Route path="/xero/view" element={<Navigate to="/xero" replace />} />
          <Route path="/customers" element={<Navigate to="/billing" replace />} />
          <Route
            path="/integrations"
            element={
              <PrivateRoute>
                <Layout>
                  <Integrations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/customers/:customerId/services"
            element={
              <PrivateRoute>
                <Layout>
                  <CustomerServices />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/xero"
            element={
              <PrivateRoute>
                <Layout>
                  <Xero />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/xero-callback"
            element={<XeroCallback />}
          />
          <Route
            path="/microsoft365"
            element={
              <PrivateRoute>
                <Layout>
                  <Microsoft365 />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/partner-center"
            element={
              <PrivateRoute>
                <Layout>
                  <PartnerCenter />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/cove"
            element={
              <PrivateRoute>
                <Layout>
                  <Cove />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/sentinelone"
            element={
              <PrivateRoute>
                <Layout>
                  <SentinelOne />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/proxmox"
            element={
              <PrivateRoute>
                <Layout>
                  <Proxmox />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <PrivateRoute>
                <Layout>
                  <Billing />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <PrivateRoute>
                <Layout>
                  <Expenses />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/whmcs"
            element={
              <PrivateRoute>
                <Layout>
                  <WHMCS />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/splynx"
            element={
              <PrivateRoute>
                <Layout>
                  <Splynx />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/portaone"
            element={
              <PrivateRoute>
                <Layout>
                  <PortaOne />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/redstor"
            element={
              <PrivateRoute>
                <Layout>
                  <Redstor />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/halopsa"
            element={
              <PrivateRoute>
                <Layout>
                  <HaloPSA />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/cloudblue"
            element={
              <PrivateRoute>
                <Layout>
                  <CloudBlue />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <Layout>
                  <UserManager />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/organizations"
            element={
              <PrivateRoute>
                <Layout>
                  <OrganizationManager />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Layout>
                  <Profile />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <PrivateRoute>
                <Layout>
                  <AuditLog />
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;

